/**
 * Review-edit application helpers — the pure "overlay pending edits onto the
 * parsed plan" half of CLW's saveReviewEdits.
 *
 * Extracted so LaunchCampaignDetails persists edits through exactly the same
 * field mappings CLW uses. These functions do the deep clone themselves and
 * never mutate their inputs, so callers can PUT the result directly:
 *
 *   TTD / DV360 / → applyLauncherResultEdits(launcherResult, edits, deletions)
 *   Google Ads      PUT /api/v2/campaigns/:id/planning-review
 *                     { editedLauncherResult }
 *   GroundTruth → applyGtConfigEdits(ctsReview.result, edits)
 *                 PUT /api/v2/campaigns/:id/gt-review-edits
 *                     { editedConfig }
 *
 * `edits` is  { rowId: { field: value } }  (parent's editedRows). The third
 * argument accepts BOTH list-op shapes the edit hook has produced over time:
 *   - list ops (current): { rowId: { field: [{ op: 'remove'|'add'|'edit', value, newValue? }] } }
 *   - legacy deletions:   { rowId: { field: [label] } }  (remove-only)
 * Legacy string arrays are normalized internally to remove ops, so both old
 * and new callers work; removes behave byte-identically to the legacy path.
 */

import { isYahooResult, applyYahooEdits } from './yahoo/yahooReview';
import { applyGtIoBuyingModel, hasGtIoBuyingModel, recordGtIoTermEdit } from './groundtruth/gtIoBuyingModel';
import { countryName, COUNTRY_BY_ID } from './dv360/dv360Constants';
import {
  removeFromPlan, addGeoToPlan, removeGeoFromPlan,
} from './dv360/dv360TargetingPlan';
import { supplyVendorName } from './ttd/ttdConstants';
import {
  buildPackageIndex, parseIoDisplayName, isImpressionBudget,
} from './dv360/dv360Parsing';
import {
  GT_FIELD_DESCRIPTOR,
  GT_FIELD_EDITORS,
  GT_ROW_TO_AG,
  GT_AUDIENCE_PLACEHOLDER,
  GT_LOCATION_GROUP_PLACEHOLDER,
  gtAudienceLabel,
  isGtAudiencePlaceholder,
} from './groundtruth/gtConstants';
import { isGadsPlatform } from './gads/gadsConstants';
import { isGadsLauncherResult } from './gads/gadsRowBuilders';
import { applyGadsEdits } from './gads/gadsEdits';
import { isFbPlatform } from './fb/fbConstants';
import { isFbLauncherResult } from './fb/fbRowBuilders';
import { applyFbEdits } from './fb/fbEdits';
import { applyDv360RowOps, applyTtdRowOps, applyGtRowOps } from './rowOps';
import { gtPlannedUnitSources, gtUsesClicks } from './groundtruth/gtPlannedUnits';
import {
  numeric,
  scaleToParent,
  dv360ImpsFromBudget,
  normalizeRowOps,
  decodeLookupValue,
  ymdToTtdIso,
  writeTtdMoney,
  ttdPacingModeFromDraft,
  writeTtdKpiTarget,
  applyTtdFreqCap,
  applyTtdKpiType,
  applyDv360Pacing,
  applyDv360FreqCap,
  writeDv360DailyCap,
  writeDv360BidMicros,
  applyDv360BidStrategy,
  applyDv360BidGoal,
  applyDv360Expansion,
  applyDv360Pixel,
  applyDv360ConvWindow,
  applyDv360IoKpiType,
  applyDv360FlightDates,
} from './sharedFieldWriters';

// Comma-separated list cell → trimmed entries ('—' placeholders dropped).
const splitCommaDraft = (draft) => String(draft || '')
  .split(',').map((s) => s.trim()).filter((s) => s && s !== '—');

// ─── GT merged-audiences list ops + entry rules ────────────────────────────
// Goal 3: the Audiences cell edits adgroups[].audiences[] as
// {id, type, name, exclude:false} records keyed by (id, type). Lookup picks
// arrive encoded `<id><US><type><US><label>` (see sharedFieldWriters).

const gtProductToken = (product) => {
  const s = String(product || '').trim().toLowerCase();
  return s === 'on premise' ? 'onpremise' : s;
};

// Rule wording lives in the descriptor's Rules sheet records — the UI's entry
// rules are documented in the same document that describes the fields.
const gtRuleMessage = (id, fallback) => (GT_FIELD_DESCRIPTOR?.rules?.[id]?.message || fallback);

const gtAudienceKey = (entry) => `${String(entry?.type || '')}\u001F${String(entry?.id || '')}`;

function gtAudienceFromPick(value) {
  const { id, type, label } = decodeLookupValue(value);
  if (!id || !type || !label) return null;
  return {
    id: String(id),
    type: String(type),
    name: label,
    exclude: false, // R16 — include-only; written explicitly so the shape matches the API
  };
}

/**
 * Single-select lookup cells (Location group, Drive-To). Ops are add /
 * replace / remove against ONE slot: a pick replaces whatever was there
 * (placeholder 92006 included — R13), remove clears it. Drive-To writes the
 * scalar label (the launcher resolves the taxonomy name, with drive_to_type
 * choosing brand vs location_group); Location group writes the object {id,
 * name} so the launcher can use the real id directly.
 */
function applyGtSinglePickOps(ag, configKey, fieldOps, warnings, rowName) {
  for (const op of fieldOps || []) {
    if (op.op === 'remove') {
      if (configKey === 'location_group' && ag._targeting_selection?.location && ag.location_group?.id) {
        // Removing the canonical group also removes its duplicate GT-array
        // representation, so save normalization cannot resurrect that pick.
        ag.audiences = (ag.audiences || []).filter((a) => !(a?.type === 'location_group'
          && String(a.id) === String(ag.location_group.id)));
      }
      if (configKey === 'location_group' && ag._targeting_selection?.neighborhood && ag.location_group?.id) {
        ag.neighborhoods = (ag.neighborhoods || []).filter((a) => !(a?.type === 'location_group'
          && String(a.id) === String(ag.location_group.id)));
      }
      ag[configKey] = null;
      if (configKey === 'tracking_visits_to') {
        delete ag.drive_to_location_group;
        ag.drive_to_type_name = null;
        ag.tracking_visits_to_array = null;
        ag.tracking_visits_to_raw = null;
      }
    } else if (op.op === 'add' || op.op === 'edit') {
      const { id, label } = decodeLookupValue(op.value == null && op.op === 'edit' ? op.newValue : op.value);
      if (op.op === 'edit') {
        const pick = decodeLookupValue(op.newValue);
        if (!pick.label) continue;
        // A location group launches by ID — a typed name decodes with no id
        // and would write a corrupted {id: null} record the launcher can't
        // use. Refuse loudly rather than staging a broken pick (the preview
        // engine gates this too; this is the applier's own backstop).
        if (configKey === 'location_group' && !pick.id) {
          warnings.push({
            rowId: rowName,
            field: configKey,
            reason: 'A location group must be picked from the taxonomy — a typed name has no id and cannot launch, so this replace was skipped.',
          });
          continue;
        }
        const wasPlaceholder = configKey === 'location_group'
          && String(ag[configKey]?.id) === GT_LOCATION_GROUP_PLACEHOLDER.id;
        ag[configKey] = configKey === 'location_group'
          ? { id: pick.id, name: pick.label }
          : pick.label;
        if (configKey === 'tracking_visits_to') writeGtDriveToPick(ag, pick);
        if (wasPlaceholder) {
          warnings.push({
            rowId: rowName,
            field: configKey,
            reason: gtRuleMessage('R13', 'Remove the placeholder location group before launching.'),
          });
        }
      } else if (label && (id || configKey === 'tracking_visits_to')) {
        const wasPlaceholder = configKey === 'location_group'
          && String(ag[configKey]?.id) === GT_LOCATION_GROUP_PLACEHOLDER.id;
        ag[configKey] = configKey === 'location_group'
          ? { id, name: label }
          : label;
        if (configKey === 'tracking_visits_to') writeGtDriveToPick(ag, decodeLookupValue(op.value));
        if (wasPlaceholder) {
          warnings.push({
            rowId: rowName,
            field: configKey,
            reason: gtRuleMessage('R13', 'Remove the placeholder location group before launching.'),
          });
        }
      }
    }
  }
}

function writeGtDriveToPick(ag, pick) {
  // The UI already holds the account-owned ID; keep it instead of resolving a
  // label again at launch. A newer typed/brand pick invalidates an older pin.
  delete ag.drive_to_location_group;
  ag.drive_to_type_name = pick.label;
  ag.tracking_visits_to_array = null;
  ag.tracking_visits_to_raw = pick.label;
  if (pick.type === 'location_group' && pick.id) {
    ag.drive_to_location_group = { id: String(pick.id), type: pick.type, name: pick.label };
    ag.drive_to_type = 'Location Group';
  } else if (pick.type === 'brand') {
    ag.drive_to_type = 'Brand';
  }
}

function applyGtAudienceListOps(ag, fieldOps) {  let list = Array.isArray(ag.audiences)
    ? ag.audiences.map((e) => ({ ...e }))
    : [];

  const removed = new Set(
    (fieldOps || []).filter((o) => o.op === 'remove').map((o) => decodeLookupValue(o.value).label),
  );
  if (removed.size) list = list.filter((e) => !removed.has(gtAudienceLabel(e)));

  for (const op of (fieldOps || [])) {
    if (op.op === 'add') {
      const entry = gtAudienceFromPick(op.value);
      if (!entry) continue;
      if (!list.some((e) => gtAudienceKey(e) === gtAudienceKey(entry))) list.push(entry);
    } else if (op.op === 'edit') {
      // Edit = REPLACE the pick. The id travels with the new record, so the
      // old entry is swapped wholesale — never relabeled in place.
      const from = decodeLookupValue(op.value).label;
      const entry = gtAudienceFromPick(op.newValue);
      if (!entry) continue;
      const idx = list.findIndex((e) => gtAudienceLabel(e) === from);
      if (idx >= 0 && !list.some((e, i) => i !== idx && gtAudienceKey(e) === gtAudienceKey(entry))) {
        list[idx] = entry;
      }
    }
  }
  ag.audiences = list;
  if ((fieldOps || []).some((op) => op.op === 'remove')) ag._audience_selection_explicit = true;
}

/**
 * Entry rules R12 / R14 / R16. Placeholders are replaced, never appended;
 * On Premise rows carry no audiences; exclude is always false. Violations are
 * corrected and reported in `warnings` so a save cannot silently mutate what
 * the reviewer picked.
 */
function enforceGtAudienceEntryRules(editedConfig, warnings) {
  const adgroups = Array.isArray(editedConfig.adgroups) ? editedConfig.adgroups : [];
  for (const ag of adgroups) {
    if (!ag || typeof ag !== 'object') continue;
    let list = Array.isArray(ag.audiences) ? ag.audiences : [];
    const hadAudiences = Array.isArray(ag.audiences);
    const name = ag.ad_group_name || '(unnamed ad group)';
    const product = gtProductToken(ag.product);

    const real = list.filter((e) => !isGtAudiencePlaceholder(e));
    const placeholders = list.filter((e) => isGtAudiencePlaceholder(e));
    if (real.length && placeholders.length) {
      list = real;
      warnings.push({
        rowId: name,
        field: 'audiences',
        reason: gtRuleMessage('R12', 'Remove the placeholder audience before launching.'),
      });
    }
    if (product === 'onpremise' && list.length) {
      list = [];
      warnings.push({
        rowId: name,
        field: 'audiences',
        reason: gtRuleMessage('R14', 'Audiences do not apply to On Premise ad groups.'),
      });
    }
    // Reviewed PDF rows must stay creation-ready after manual removals too.
    // Legacy/CTS empty selections retain their established behavior.
    const reviewedPdf = editedConfig._instruction_enrichment?.instruction_snapshot?.source_type === 'pdf';
    const ownLocation = reviewedPdf && !!ag._targeting_selection?.location;
    const neighborhood = reviewedPdf && !!ag._targeting_selection?.neighborhood;
    if (neighborhood) {
      list = list.filter((a) => !isGtAudiencePlaceholder(a));
      const groups = (ag.neighborhoods || []).filter((a) => a?.type === 'location_group' && a.id && String(a.id) !== '92006');
      const ids = [...new Set(groups.map((a) => String(a.id)))];
      if ((!ag.location_group?.id || String(ag.location_group.id) === '92006') && ids.length === 1) {
        ag.location_group = { id: String(groups[0].id), name: groups[0].name || '' };
      }
      if (ag.neighborhoods) {
        ag.neighborhoods = ag.neighborhoods.filter((a) => !(a?.type === 'location_group'
          && (String(a.id) === String(ag.location_group?.id) || String(a.id) === '92006')));
        if (ag.neighborhoods.length) {
          warnings.push({ rowId: name, field: 'location_group',
            reason: 'Neighborhood targeting requires exactly one location group. Resolve conflicting selections before launch.' });
        }
      }
    }
    if (ownLocation) {
      // The separate single-select cell is canonical. Duplicate array entries
      // are removed; conflicting IDs stay visible and block launch, never guessed.
      const groups = list.filter((a) => a?.type === 'location_group' && a.id);
      const ids = [...new Set(groups.map((a) => String(a.id)))];
      if (!ag.location_group?.id && ids.length === 1) {
        const group = groups[0];
        ag.location_group = { id: String(group.id), name: group.name || group.label || '' };
      }
      list = list.filter((a) => !isGtAudiencePlaceholder(a)
        && !(a?.type === 'location_group' && String(a.id) === String(ag.location_group?.id)));
      if (list.some((a) => a?.type === 'location_group')) {
        warnings.push({ rowId: name, field: 'location_group',
          reason: 'Location Audience requires exactly one location group. Resolve conflicting audience/group selections before launch.' });
      }
    }
    const targetingProduct = String(ag.targeting_product || '').toLowerCase();
    const intentProduct = targetingProduct.replace(/[_-]+/g, ' ');
    const dedicatedProduct = /\b(zto|predictive|zerotoone)\b|proximity|on premise/.test(intentProduct)
      || (/location/.test(intentProduct) && /audience/.test(intentProduct));
    const thirdPartyName = !dedicatedProduct && /(?:^|[^a-z0-9])(?:3p|purchase(?:d)?[\s_-]+based|third[\s_-]+party|3rd[\s_-]+party)[\s_-]+(?:audiences?|aduiences?)(?=$|[^a-z0-9])/i.test(name);
    const requiresAudience = /^(custom|behavioral?|3rd party|high indexing)\b/.test(targetingProduct) || thirdPartyName;
    if (reviewedPdf && product !== 'onpremise' && requiresAudience && !list.length) {
      list = [{ ...GT_AUDIENCE_PLACEHOLDER }];
      warnings.push({ rowId: name, field: 'audiences',
        reason: 'No real audiences are assigned; one audience placeholder is required for creation.' });
    }
    if (reviewedPdf && requiresAudience && product !== 'onpremise' && !real.length && placeholders.length) {
      list = [{ ...GT_AUDIENCE_PLACEHOLDER }];
    }
    const requiresLocation = product === 'onpremise' || /^location (audience|based)/.test(targetingProduct) || neighborhood;
    if (reviewedPdf && requiresLocation && !ag.location_group?.id) {
      ag.location_group = { ...GT_LOCATION_GROUP_PLACEHOLDER };
      warnings.push({ rowId: name, field: 'location_group',
        reason: 'No location group is assigned; Placeholder Location Group is required for creation.' });
    }
    for (const entry of list) {
      if (entry.exclude !== false) {
        const wasTrue = entry.exclude === true;
        entry.exclude = false;
        if (wasTrue) {
          warnings.push({
            rowId: name,
            field: 'audiences',
            reason: gtRuleMessage('R16', 'Audience exclusion is not supported.'),
          });
        }
      }
    }
    // Don't introduce an `audiences` key on configs that never had one —
    // legacy configs are byte-stable on unrelated saves.
    if (hadAudiences || list.length) ag.audiences = list;
  }
}

/**
 * Entry warnings for the pre-existing rules R1 / R3 / R6 / R7. These are
 * READ-ONLY checks — the converter and CTS validator still own the block
 * behaviour; the grid surfaces the same wording from the descriptor at save
 * time so a reviewer sees the problem before launch, not after.
 */
function collectGtEntryRuleWarnings(editedConfig, warnings) {
  for (const ag of Array.isArray(editedConfig.adgroups) ? editedConfig.adgroups : []) {
    if (!ag || typeof ag !== 'object') continue;
    const name = ag.ad_group_name || '(unnamed ad group)';

    // R1 — end date must be later than start date.
    const start = String(ag.start_date || '');
    const end = String(ag.end_date || '');
    if (start && end && end <= start) {
      warnings.push({ rowId: name, field: 'end_date', reason: gtRuleMessage('R1', 'End date must be after the start date.') });
    }

    // R3 — a CTV package must never carry mobile-app inventory.
    const pkgName = String(ag.package_name || '').toLowerCase();
    if (pkgName.includes('ctv')) {
      const creativeType = String(ag.creative_type || '').toLowerCase();
      const deviceTypes = String(ag.device_types || '').toLowerCase();
      if (creativeType !== 'ctv' || deviceTypes !== 'ctv') {
        warnings.push({
          rowId: name,
          field: 'creative_type',
          reason: gtRuleMessage('R3', 'A CTV package cannot carry mobile-app inventory.'),
        });
      }
    }

    // R6 — On Premise needs a proximity mode.
    if (gtProductToken(ag.product) === 'onpremise' && !ag.proximity_mode) {
      warnings.push({ rowId: name, field: 'proximity_mode', reason: gtRuleMessage('R6', 'On Premise ad groups need a proximity mode.') });
    }

    // R7 — product must match the targeting_product family.
    const product = gtProductToken(ag.product);
    const tp = String(ag.targeting_product || '').toLowerCase();
    if (['audience', 'onpremise', 'neighborhood', 'geotargeting'].includes(product)) {
      const expectedByHint = {
        audience: (hint) => /location audience|custom audience|behavioral|3rd party|retargeting|high indexing|audience/.test(hint),
        onpremise: (hint) => hint.includes('on premise'),
        neighborhood: (hint) => hint.includes('neighborhood'),
        geotargeting: (hint) => hint.includes('geotargeting'),
      };
      if (tp && !expectedByHint[product](tp)) {
        warnings.push({
          rowId: name,
          field: 'targeting_product',
          reason: gtRuleMessage('R7', 'Product must match the targeting product.'),
        });
      }
    }
  }
}

/**
 * R15 — the campaign-level CTS arrays are DERIVED from the ad group picks,
 * never authored independently. Only custom_audience / third_party / segment
 * records belong in those sheets; brand / location_group / behavior picks
 * stay on the ad group payload only.
 */
function deriveGtCtsAudiences(editedConfig) {
  const custom = [];
  const thirdParty = [];
  const seenCustom = new Set();
  const seenThird = new Set();
  for (const ag of Array.isArray(editedConfig.adgroups) ? editedConfig.adgroups : []) {
    const agName = ag?.ad_group_name || '';
    for (const entry of Array.isArray(ag?.audiences) ? ag.audiences : []) {
      if (isGtAudiencePlaceholder(entry)) continue;
      const name = String(entry?.name || '').trim();
      if (!name) continue;
      const context = `${agName}_${name}`;
      if (entry?.type === 'custom_audience' && !seenCustom.has(context)) {
        seenCustom.add(context);
        custom.push({ name, link: null, context, placeholder_id: '49124' });
      } else if ((entry?.type === 'third_party' || entry?.type === 'segment') && !seenThird.has(context)) {
        seenThird.add(context);
        thirdParty.push({ name, context, placeholder_id: '49124' });
      }
    }
  }
  editedConfig.custom_audiences = custom;
  editedConfig.third_party_audiences = thirdParty;
}

/** Rule / placeholder corrections the applier made, for the caller to surface. */
export function gtEditWarnings(editedConfig) {
  return editedConfig?.__gtWarnings || [];
}

/**
 * GroundTruth: overlay row-keyed edits onto a deep clone of ctsReview.result.
 * rowId format is `gt-ag-${idx}` (see buildGtReviewRows) so edits address the
 * adgroups array positionally. Field-key renames go through GT_ROW_TO_AG;
 * everything else is identity. Numeric/currency editors coerce to Number so
 * the CTS validator sees a number, not a "$25.00" string — and a draft
 * `numeric` cannot parse is a NO-OP: this used to fall back to writing the raw
 * draft, which (now that numeric rejects instead of coercing) would put the
 * string "abc" — or "" — where the CTS validator expects a budget.
 */
/**
 * Row id the campaign-configuration card stages its edits under. Campaign
 * fields aren't grid rows — this sentinel lets them ride the same editedRows
 * state, pending-count, save and undo machinery as ad-group cell edits.
 */
export const GT_CAMPAIGN_ROW_ID = 'gt-campaign';

export function applyGtConfigEdits(ctsResult, edits, rowOps = [], listOpsOrDeletions = []) {
  const editedConfig = JSON.parse(JSON.stringify(ctsResult || {}));
  const dropped = [];
  // Campaign-scope edits from the summary card: keyed by campaign.<key>
  // directly (no row/field rename layer), written before the ad-group loops
  // which only match `gt-ag-<idx>` ids.
  const campaignFields = (edits || {})[GT_CAMPAIGN_ROW_ID];
  if (campaignFields && typeof campaignFields === 'object') {
    editedConfig.campaign = { ...(editedConfig.campaign || {}) };
    for (const [key, value] of Object.entries(campaignFields)) {
      if (key === 'buying_model_raw' && hasGtIoBuyingModel(editedConfig)
          && (!['CPM', 'CPC'].includes(String(value).toUpperCase())
            || (editedConfig._io_buying_model.source_model === 'CPC' && String(value).toUpperCase() !== 'CPC'))) {
        dropped.push({ rowId: GT_CAMPAIGN_ROW_ID, field: key, value,
          reason: 'PDF IOs use CPM or CPC. An IO containing CPC media must use CPC for every ad group.' });
        continue;
      }
      editedConfig.campaign[key] = value;
    }
  }
  // Campaign-level buying-model edits propagate before planned-unit writes.
  applyGtIoBuyingModel(editedConfig);
  // Edits this function REFUSED to write. It used to drop them silently, which
  // is the worst possible way to fail here: the PUT still succeeded, the grid
  // still said "Saved", and the cell reverted on the next read with no
  // explanation — and the bulk-edit agent reported "applied and saved" for a
  // change that never happened. Typing a package NAME into the numeric Package
  // column hit this every time.
  // Resolve before name/package edits so a simultaneous rename cannot detach
  // the planning count from its original CTS line item.
  const plannedSources = gtPlannedUnitSources(editedConfig);
  const plannedPackages = new Map((editedConfig.adgroups || []).map((ag, index) => [ag, plannedSources[index]?.pkg]));
  const writeFields = (ag, fields, rowId) => {
    for (const [rowKey, value] of Object.entries(fields || {})) {
      if (rowKey === 'impression_clicks') continue; // Written after bid-type edits below.
      const descriptor = GT_FIELD_DESCRIPTOR.fields[rowKey];
      if (descriptor?.scope === 'derived' && descriptor.editable === false) {
        dropped.push({ rowId, field: rowKey, value, reason: descriptor.readOnlyReason || 'Read-only planning value.' });
        continue;
      }
      const agKey = GT_ROW_TO_AG[rowKey] || rowKey;
      if (hasGtIoBuyingModel(editedConfig) && ['bid_type', 'optimization_strategy'].includes(agKey)
          && String(value).toLowerCase() !== String(ag[agKey]).toLowerCase()) {
        dropped.push({ rowId, field: rowKey, value,
          reason: `This IO uses ${editedConfig.campaign.buying_model_raw} for every ad group. Change the campaign Buying Model instead.` });
        continue;
      }
      const editorType = GT_FIELD_EDITORS[rowKey];
      if (editorType === 'currency' || editorType === 'number') {
        const n = numeric(value);
        if (n == null) {
          dropped.push({
            rowId,
            field: rowKey,
            value,
            reason: `${rowKey} is a number on GroundTruth, and "${value}" isn't one`
              + (rowKey === 'package'
                // The specific confusion worth naming: Package is the numeric
                // grouping key, Package name is the label the launcher creates
                // the ad package with.
                ? '. To name the package, edit "Package name" instead — Package is the numeric grouping key.'
                : '.'),
          });
          continue;
        }
        ag[agKey] = n;
        recordGtIoTermEdit(editedConfig, ag, agKey, n);
      } else {
        ag[agKey] = value;
      }
    }
    if (Object.prototype.hasOwnProperty.call(fields || {}, 'impression_clicks')) {
      const value = fields.impression_clicks;
      const n = typeof value === 'boolean' ? null : numeric(value);
      if (!Number.isSafeInteger(n) || n < 0) {
        dropped.push({ rowId, field: 'impression_clicks', value,
          reason: 'Impression/Clicks must be a whole number of zero or greater.' });
        return;
      }
      const pkg = plannedPackages.has(ag) ? plannedPackages.get(ag)
        : gtPlannedUnitSources(editedConfig)[editedConfig.adgroups.indexOf(ag)]?.pkg;
      const isClicks = gtUsesClicks(ag, pkg, editedConfig.campaign);
      ag[isClicks ? 'clicks' : 'impressions'] = n;
      if (isClicks) recordGtIoTermEdit(editedConfig, ag, 'clicks', n);
      if (pkg) {
        // CTS uses `impressions` for its combined Impressions/Clicks column.
        pkg.impressions = n;
        if (isClicks && Object.prototype.hasOwnProperty.call(pkg, 'clicks')) pkg.clicks = n;
      }
    }
  };
  const adgroupArr = Array.isArray(editedConfig.adgroups) ? editedConfig.adgroups : [];
  for (const [rowId, fields] of Object.entries(edits || {})) {
    const m = /^gt-ag-(\d+)$/.exec(rowId);
    if (!m) continue;
    const ag = adgroupArr[Number(m[1])];
    if (!ag) continue;
    writeFields(ag, fields, rowId);
  }
  // List ops before row ops, for the same positional reason as scalar edits:
  // a delete renumbers every later row, so ops must resolve against the
  // ordering they were staged on.
  const gtWarnings = [];
  let audiencesTouched = false;
  for (const [rowId, fields] of Object.entries(listOpsOrDeletions || {})) {
    const m = /^gt-ag-(\d+)$/.exec(rowId);
    if (!m) continue;
    const ag = adgroupArr[Number(m[1])];
    if (!ag) continue;
    const fieldOps = normalizeRowOps(fields);
    const audienceOps = fieldOps.audiences || [];
    if (audienceOps.length) {
      applyGtAudienceListOps(ag, audienceOps);
      audiencesTouched = true;
    }
    const locationGroupOps = fieldOps.location_group || [];
    if (locationGroupOps.length) {
      applyGtSinglePickOps(ag, 'location_group', locationGroupOps, gtWarnings, ag.ad_group_name);
    }
    const driveToOps = fieldOps.tracking_visits_to || [];
    if (driveToOps.length) {
      applyGtSinglePickOps(ag, 'tracking_visits_to', driveToOps, gtWarnings, ag.ad_group_name);
    }
  }

  // Row ops last — GT row ids are purely positional (`gt-ag-${idx}`), so a
  // delete renumbers every later row and must not run before the value edits
  // above have been resolved against the original ordering.
  if (Array.isArray(rowOps) && rowOps.length) {
    const { created } = applyGtRowOps(editedConfig, rowOps);
    const after = Array.isArray(editedConfig.adgroups) ? editedConfig.adgroups : [];
    for (const { rowId, overrides } of created) {
      if (!overrides || !Object.keys(overrides).length) continue;
      const m = /^gt-ag-(\d+)$/.exec(rowId);
      if (!m) continue;
      const ag = after[Number(m[1])];
      if (ag) writeFields(ag, overrides, rowId);
    }
  }
  // Finalize AFTER all scalar/list/row edits, including newly created rows.
  // Required PDF targeting falls back only when no real assignments remain.
  enforceGtAudienceEntryRules(editedConfig, gtWarnings);
  applyGtIoBuyingModel(editedConfig);
  if (audiencesTouched || (Array.isArray(rowOps) && rowOps.length)) deriveGtCtsAudiences(editedConfig);
  collectGtEntryRuleWarnings(editedConfig, gtWarnings);
  // `dropped` is a property on the returned config rather than a second return
  // value so every existing caller keeps working unchanged, and a caller that
  // wants to report refusals reads it. Non-enumerable so it cannot leak into the
  // JSON that gets PUT to the server.
  Object.defineProperty(editedConfig, '__droppedEdits', {
    value: dropped, enumerable: false, writable: false,
  });
  Object.defineProperty(editedConfig, '__gtWarnings', {
    value: gtWarnings, enumerable: false, writable: false,
  });
  return editedConfig;
}

/** Edits applyGtConfigEdits refused to write, for the caller to surface. */
export function droppedGtEdits(editedConfig) {
  return editedConfig?.__droppedEdits || [];
}

// ─── TTD field writers ────────────────────────────────────────────────
// THE WRITE TARGET IS WHAT THE ROW BUILDER READS (ttd/ttdRowBuilders.js) —
// every writer here targets the exact key buildTtdReviewRows renders, so a
// saved edit re-renders as typed. Cells whose value the builder derives from
// the parent campaign (dates / budget / imps / dailyCap) write the parent,
// scaled back up by sibling count where the row shows a per-ad-group share.

// applyTtdFreqCap (NewFrequencyConfigs) and applyTtdKpiType (the ROIGoal
// wrapper-key rename) moved to sharedFieldWriters.js — the copy grid's TTD
// applier writes the same two keys against the same row builder, so one
// implementation is the only way they can't drift.

// MmgyAudiences.ExpandWithThirdPartyAudienceSearch, edited through the
// cell's "Expand: true/false" rendering (bare booleans tolerated).
function applyTtdExpansion(ag, draft) {
  const s = String(draft).trim().toLowerCase().replace(/^expand[:\s]*/, '');
  let flag = null;
  if (/^(true|yes|on|1)$/.test(s)) flag = true;
  else if (/^(false|no|off|0)$/.test(s)) flag = false;
  if (flag == null) return;
  const ma = ag.MmgyAudiences || (ag.MmgyAudiences = {});
  ma.ExpandWithThirdPartyAudienceSearch = flag;
}

// MarketplaceOptOut, edited through the cell's 'Marketplace opt-out' / '—'
// rendering (booleans tolerated). Unrecognized input → no-op.
function applyTtdBrandSafety(ag, draft) {
  const s = String(draft).trim().toLowerCase();
  if (s.includes('opt-out') || s.includes('opt out') || /^(true|yes|on)$/.test(s)) {
    ag.MarketplaceOptOut = true;
  } else if (['false', 'no', 'off', 'none', '—', '-', ''].includes(s)) {
    ag.MarketplaceOptOut = false;
  }
}

// TTD scalar edits → AdGroups[] / parent Campaigns[] properties. `rowId` is
// the grid's row key (either adGroupId spelling), used to resolve the parent
// campaign whose AdGroups list carries it.
function applyTtdFields(edited, ag, rowId, fields) {
  const parent = (edited.Campaigns || [])
    .find((c) => Array.isArray(c.AdGroups) && c.AdGroups.includes(rowId)) || null;
  const sibs = (parent && parent.AdGroups.length) || 1;

  if (fields.name != null && String(fields.name).trim()) {
    // Write the spelling(s) the entity already carries (MMGY lowercase vs
    // copy-template PascalCase) so both stay in sync and no new key is
    // introduced into a strict template.
    const v = String(fields.name).trim();
    if ('adGroupName' in ag || !('AdGroupName' in ag)) ag.adGroupName = v;
    if ('AdGroupName' in ag) ag.AdGroupName = v;
  }
  if (fields.tactic) ag.tactic = fields.tactic;
  if (fields.category != null && String(fields.category).trim()) {
    ag.FunnelLocation = String(fields.category).trim();
  }
  // cpm and bidFloor are the same underlying value (ag.BaseBidCPM) surfaced
  // in two columns — both write the key the builder actually reads. (The old
  // writer targeted BaseBidCPMInAdvertiserCurrency, which nothing reads.)
  if (fields.cpm != null) writeTtdMoney(ag, 'BaseBidCPM', fields.cpm);
  if (fields.bidFloor != null) writeTtdMoney(ag, 'BaseBidCPM', fields.bidFloor);
  if (fields.bidCeiling != null) writeTtdMoney(ag, 'MaxBidCPM', fields.bidCeiling);
  if (fields.pacing != null) {
    const mode = ttdPacingModeFromDraft(fields.pacing);
    if (mode) ag.PacingMode = mode;
  }
  if (fields.devices != null) ag.DeviceType = splitCommaDraft(fields.devices);
  if (fields.environment != null) ag.AdEnvironment = splitCommaDraft(fields.environment);
  if (fields.freqCap != null) applyTtdFreqCap(ag, fields.freqCap);
  if (fields.expansion != null) applyTtdExpansion(ag, fields.expansion);
  if (fields.brandSafety != null) applyTtdBrandSafety(ag, fields.brandSafety);
  if (fields.kpi != null) applyTtdKpiType(ag, fields.kpi);
  if (fields.kpiTarget != null) writeTtdKpiTarget(ag, fields.kpiTarget);
  // NO entityStatus WRITE. `entityStatus` is denylisted in TTD_DENYLIST so the
  // grid never offers the cell — but the denylist is not the only way a field
  // reaches this applier (LaunchCampaignDetails' applyFlagSuggestion stages
  // edits straight into editedRows by field name, without consulting the
  // editable set), and on TTD there is no creator-side pause backstop
  // whatsoever. Refusing the field HERE is what actually closes the path from
  // a review-grid edit to a live, bidding TTD campaign. Activation belongs to
  // the launch action.

  // Parent-campaign cells: the row shows each value's per-ad-group share
  // (builder divides by sibling count), so the edit writes share × siblings
  // back — through the shared scaleToParent so CellDetailPanel's pre-commit
  // preview computes the identical total. Editing several rows of one campaign
  // in a single save is last-writer-wins by construction. No parent → no-op.
  if (parent) {
    if (fields.start != null) {
      const iso = ymdToTtdIso(fields.start);
      if (iso) parent.StartDate = iso;
    }
    if (fields.end != null) {
      const iso = ymdToTtdIso(fields.end);
      if (iso) parent.EndDate = iso;
    }
    if (fields.budget != null) {
      const total = scaleToParent(numeric(fields.budget), sibs);
      if (total != null) writeTtdMoney(parent, 'Budget', total);
    }
    if (fields.imps != null) {
      const total = scaleToParent(numeric(fields.imps), sibs);
      if (total != null) parent.BudgetInImpressions = Math.round(total);
    }
    if (fields.dailyCap != null) {
      const total = scaleToParent(numeric(fields.dailyCap), sibs);
      if (total != null) parent.DailyBudgetInImpressions = Math.round(total);
    }
  }
}

// ─── DV360 field writers ──────────────────────────────────────────────
// Same principle: the write target is what buildDv360ReviewRows reads.

// Match an IO to its packages[] entry the same three ways the row builder
// does (packageKey → PKID# → package name).
function matchIoPackage(edited, io) {
  const packages = Array.isArray(edited.packages) ? edited.packages : [];
  const { pkgByKey, pkgByIoMatch } = buildPackageIndex(packages);
  const meta = parseIoDisplayName(io.displayName || '');
  const pkg = (io.packageKey && pkgByKey.get(io.packageKey))
    || (meta.pkId && pkgByIoMatch.get(`pk:${meta.pkId}`))
    || (meta.packageName && pkgByIoMatch.get(`name:${meta.packageName.toLowerCase()}`))
    || null;
  return { meta, pkg };
}

// The row's CPM the way the builder derives it: displayName "($7.5)" rate
// first, then the matched package's rate.
function deriveIoCpm(edited, io) {
  const { meta, pkg } = matchIoPackage(edited, io);
  if (meta.rate != null) return meta.rate;
  const rate = pkg ? Number(pkg.rate) : NaN;
  return Number.isFinite(rate) ? rate : null;
}

// cpm → the matched package's rate. The row builder PREFERS the rate parsed
// from the IO displayName's "($7.5)" suffix, so when the name carries one it
// is rewritten in place too (keeping the pushed IO name truthful about its
// rate). No package match and no name rate → no write target → no-op.
function applyDv360Cpm(edited, io, value) {
  const n = numeric(value);
  if (n == null || n <= 0) return;
  const { meta, pkg } = matchIoPackage(edited, io);
  if (pkg) pkg.rate = n;
  if (meta.rate != null && typeof io.displayName === 'string') {
    io.displayName = io.displayName.replace(/\(\$?[\d.]+\)/, `($${n})`);
  }
}

// imps → the IO's impression storage, mirroring getIoImpressions' read
// order: segments carrying budgetAmountInImpressions are scaled
// proportionally (last positive segment absorbs the rounding remainder so
// the sum is exact); otherwise the matched package's units take the total;
// with neither there is no write target → no-op. `total` is already
// share × siblings.
function applyDv360Imps(edited, io, total) {
  if (!Number.isFinite(total) || total < 0) return;
  const segs = Array.isArray(io.budget?.budgetSegments) ? io.budget.budgetSegments : [];
  const impressionUnit = isImpressionBudget(io);
  const segAmt = (seg) => Number(seg.budgetAmountInImpressions ?? seg.budgetAmountImpressions
    ?? (impressionUnit ? seg.budgetAmountMicros : undefined) ?? 0);
  const positive = segs.filter((seg) => segAmt(seg) > 0);
  const current = positive.reduce((s, seg) => s + segAmt(seg), 0);
  if (current > 0) {
    let allocated = 0;
    positive.forEach((seg, i) => {
      const next = i === positive.length - 1
        ? total - allocated
        : Math.round((segAmt(seg) / current) * total);
      allocated += next;
      if (Object.prototype.hasOwnProperty.call(seg, 'budgetAmountInImpressions')) {
        seg.budgetAmountInImpressions = next;
      } else if (impressionUnit
        && !Object.prototype.hasOwnProperty.call(seg, 'budgetAmountImpressions')) {
        // Imported IO: write the count back where DV360 itself keeps it, as a
        // string, which is how the API returns int64 and how it goes back out.
        seg.budgetAmountMicros = String(next);
      } else {
        seg.budgetAmountImpressions = next;
      }
    });
    return;
  }
  const { pkg } = matchIoPackage(edited, io);
  if (pkg && pkg.units != null) pkg.units = total;
}

// applyDv360BidStrategy / applyDv360BidGoal / applyDv360Expansion moved to
// sharedFieldWriters.js — bidStrategy, bidGoal and targetingExpansion are keys
// the DV360 COPY template carries too (and the validator's line-item
// allowlist names), so the copy applier writes them through the very same
// functions.

// devices → deviceTargetingExclude.excludedDeviceTypes, parsing the cell's
// "excl. smart phone, tablet" rendering back into DEVICE_TYPE_* enums.
// 'all devices' (the no-exclusion rendering) clears the list. Any
// unrecognized device token → whole edit no-ops (enum contract).
const DV360_DEVICE_TYPES = new Set(['COMPUTER', 'CONNECTED_TV', 'SMART_PHONE', 'TABLET', 'CONNECTED_DEVICE']);
function applyDv360Devices(li, draft) {
  const s = String(draft).trim();
  const lower = s.toLowerCase();
  if (!s || lower === 'all devices' || lower === '—') {
    const dte = li.deviceTargetingExclude || (li.deviceTargetingExclude = {});
    dte.excludedDeviceTypes = [];
    return;
  }
  const tokens = lower.replace(/^excl\.?\s*/, '').split(',')
    .map((t) => t.trim().toUpperCase().replace(/[\s-]+/g, '_').replace(/^DEVICE_TYPE_/, ''))
    .filter(Boolean);
  if (!tokens.length || tokens.some((t) => !DV360_DEVICE_TYPES.has(t))) return;
  const dte = li.deviceTargetingExclude || (li.deviceTargetingExclude = {});
  dte.excludedDeviceTypes = tokens.map((t) => `DEVICE_TYPE_${t}`);
}

// environment → environmentTargeting.includedEnvironment, re-prefixed from
// the cell's "web optimized, app" rendering. Same enum contract as devices.
const DV360_ENVIRONMENTS = new Set(['WEB_OPTIMIZED', 'WEB_NOT_OPTIMIZED', 'APP']);
function applyDv360Environment(li, draft) {
  const s = String(draft).trim();
  if (!s || s === '—') {
    const et = li.environmentTargeting || (li.environmentTargeting = {});
    et.includedEnvironment = [];
    return;
  }
  const tokens = s.toLowerCase().split(',')
    .map((t) => t.trim().toUpperCase().replace(/[\s-]+/g, '_').replace(/^ENVIRONMENT_/, ''))
    .filter(Boolean);
  if (!tokens.length || tokens.some((t) => !DV360_ENVIRONMENTS.has(t))) return;
  const et = li.environmentTargeting || (li.environmentTargeting = {});
  et.includedEnvironment = tokens.map((t) => `ENVIRONMENT_${t}`);
}

// parseDv360ConvWindow / applyDv360Pixel / applyDv360ConvWindow moved to
// sharedFieldWriters.js — conversionCounting is on the DV360 validator's
// line-item allowlist and the copy template carries the source's, so the copy
// grid edits the pixel and its lookback windows through the same writers.

// clickURL → the clickUrl of the result-level assets this line item
// references (li.assets is an array of assetIds; the launcher resolves the
// same references when assigning creatives, and the preprocessor/validator
// read asset.clickUrl). The row builder renders the same source, so this
// round-trips. No referenced assets → no write target → no-op.
function applyDv360ClickUrl(edited, li, draft) {
  const url = String(draft).trim();
  if (!url) return;
  const refs = new Set((Array.isArray(li.assets) ? li.assets : []).map((r) => String(r)));
  if (!refs.size) return;
  for (const asset of (Array.isArray(edited.assets) ? edited.assets : [])) {
    if (asset && asset.assetId != null && refs.has(String(asset.assetId))) {
      asset.clickUrl = url;
    }
  }
}

// applyDv360IoKpiType moved to sharedFieldWriters.js — `kpi` is on the
// validator's INSERTION ORDER allowlist and the copy IO template always emits
// one, so the copy grid edits the KPI type through the same writer.

// DV360 scalar edits → lineItems[] / parent IO / packages[] / assets[].
// `rowId` resolves the parent IO (whose lineItems list carries it) for the
// IO-scoped cells; rows without a resolvable IO no-op those cells only.
function applyDv360Fields(edited, li, rowId, fields) {
  const io = (edited.insertionOrders || [])
    .find((x) => Array.isArray(x.lineItems) && x.lineItems.includes(rowId)) || null;
  const sibs = (io && io.lineItems.length) || 1;

  if (fields.name) li.displayName = fields.name;
  if (fields.tactic) li.tactic = fields.tactic;
  // Strategy swap FIRST so a bidFloor/bidGoal edit in the same batch lands
  // inside the new wrapper key rather than the old one.
  if (fields.bidStrategy != null) applyDv360BidStrategy(li, fields.bidStrategy);
  if (fields.bidFloor != null) writeDv360BidMicros(li, fields.bidFloor);
  if (fields.bidGoal != null) applyDv360BidGoal(li, fields.bidGoal);
  if (fields.viewability != null) {
    const n = numeric(fields.viewability);
    if (n != null) li.viewability = n;
  }
  if (fields.dailyCap != null) writeDv360DailyCap(li, fields.dailyCap);
  if (fields.pacing != null) applyDv360Pacing(li, fields.pacing);
  // NO entityStatus WRITE — see the note in applyTtdFields. DV360's creator
  // does force the parent campaign to ENTITY_STATUS_PAUSED, but a review grid
  // relying on a downstream creator to undo what it wrote is not a guarantee,
  // and all four surfaces state the same rule: activation is the launch
  // action's job, not a cell.
  if (fields.freqCap != null) applyDv360FreqCap(li, fields.freqCap);
  if (fields.expansion != null) applyDv360Expansion(li, fields.expansion);
  if (fields.devices != null) applyDv360Devices(li, fields.devices);
  if (fields.environment != null) applyDv360Environment(li, fields.environment);
  // Pixel before convWindow so a fresh floodlight config created by the
  // pixel edit is there for the window edit to refine.
  if (fields.pixel != null) applyDv360Pixel(li, fields.pixel, fields.convWindow);
  if (fields.convWindow != null) applyDv360ConvWindow(li, fields.convWindow);
  if (fields.clickURL != null) applyDv360ClickUrl(edited, li, fields.clickURL);

  // IO-scoped cells (dates / kpi / cpm / imps / budget). Order matters:
  // cpm first so a budget edit in the same batch derives against the new
  // rate; budget last (it rewrites imps) so an explicit imps edit loses to
  // the more-derived budget edit deliberately.
  applyDv360FlightDates(edited, io, fields);
  if (io && fields.kpi != null) applyDv360IoKpiType(io, fields.kpi);
  if (io && fields.kpiTarget != null) {
    const n = numeric(fields.kpiTarget);
    if (n != null) {
      const kpi = io.kpi || (io.kpi = {});
      kpi.kpiAmountMicros = Math.round(n * 1e6);
    }
  }
  if (io && fields.cpm != null) applyDv360Cpm(edited, io, fields.cpm);
  if (io && fields.imps != null) {
    const total = scaleToParent(numeric(fields.imps), sibs);
    if (total != null) applyDv360Imps(edited, io, Math.round(total));
  }
  // budget is a DERIVED cell (cpm × imps / 1000) — persist it by solving for
  // impressions against the row's effective CPM. No positive CPM → no-op.
  // Both steps go through the shared helpers (dv360ImpsFromBudget then
  // scaleToParent) so DV360_SCALED_FIELDS' preview lands on the same number.
  if (io && fields.budget != null) {
    const cpm = fields.cpm != null ? numeric(fields.cpm) : deriveIoCpm(edited, io);
    const perRowImps = dv360ImpsFromBudget(numeric(fields.budget), cpm);
    const total = scaleToParent(perRowImps, sibs);
    if (total != null) applyDv360Imps(edited, io, total);
  }
}

// TTD list deletions. Audiences/keywords live per-ad-group under
// MmgyAudiences; geo lives on the shared CommonGeoLists entry keyed by
// ag.geography.geographyId, where each GeoSegment renders with
// MetroName || CityName || RegionName || CountryName || Name — so we drop
// segments whose visible label matches a pending deletion.
function applyTtdDeletions(edited, ag, dels) {
  const ma = ag.MmgyAudiences || {};
  if (dels.audiences?.length && Array.isArray(ma.ResolvedAudiences)) {
    const drop = new Set(dels.audiences);
    ma.ResolvedAudiences = ma.ResolvedAudiences.filter((r) => !drop.has(r.FullPath));
  }
  if (dels.keywords?.length && Array.isArray(ma.ThirdPartyAudiences)) {
    const drop = new Set(dels.keywords);
    ma.ThirdPartyAudiences = ma.ThirdPartyAudiences.filter((k) => !drop.has(k));
  }
  const dropGeoInc = new Set(dels.geoInclude || []);
  const dropGeoExc = new Set(dels.geoExclude || []);
  if ((dropGeoInc.size || dropGeoExc.size) && Array.isArray(edited.CommonGeoLists)) {
    const hit = ttdGeoListFor(edited, ag);
    if (hit?.TargetGeoSegmentIds?.GeoSegmentIds && dropGeoInc.size) {
      hit.TargetGeoSegmentIds.GeoSegmentIds = hit.TargetGeoSegmentIds.GeoSegmentIds
        .filter((s) => !dropGeoInc.has(ttdGeoLabel(s)));
    }
    if (hit?.BlockGeoSegmentIds?.GeoSegmentIds && dropGeoExc.size) {
      hit.BlockGeoSegmentIds.GeoSegmentIds = hit.BlockGeoSegmentIds.GeoSegmentIds
        .filter((s) => !dropGeoExc.has(ttdGeoLabel(s)));
    }
  }
}

// The label a GeoSegment renders with in the grid — must stay in lockstep with
// ttdRowBuilders' labelsFromSegs, since removes and adds both match on it.
// `Name` is what the TTD geosegment search returns, so a picked segment reads
// back with the same label the user chose.
const ttdGeoLabel = (s) => s?.MetroName || s?.CityName || s?.RegionName || s?.CountryName || s?.Name;

/** The shared CommonGeoLists entry this ad group targets, if any. */
function ttdGeoListFor(edited, ag) {
  const geoListId = ag?.geography?.geographyId;
  if (!geoListId || !Array.isArray(edited?.CommonGeoLists)) return null;
  return edited.CommonGeoLists.find((g) => g.id === geoListId) || null;
}

// ─── List-op normalization ────────────────────────────────────────────
// The panel's staged ops are { op: 'remove'|'add'|'edit', value, newValue? };
// legacy callers pass bare label strings (remove-only). Everything funnels
// through ops internally — normalizeRowOps lives in sharedFieldWriters.js so
// gads/gadsEdits.js (which the copy grid calls directly) applies the same rule.

// Per-row remove ops → the legacy per-row dels shape ({ field: [label] }).
const removesOf = (fieldOps) => {
  const dels = {};
  for (const [field, ops] of Object.entries(fieldOps)) {
    const removed = ops.filter((o) => o.op === 'remove').map((o) => o.value);
    if (removed.length) dels[field] = removed;
  }
  return dels;
};

// ─── TTD list ops ─────────────────────────────────────────────────────

// How a MmgyDeals entry renders in the pmp cell (see buildTtdReviewRows):
// DealName wins, else "<vendor> — <dealId>", else the bare dealId. Removes
// and edits match on this exact label.
const ttdDealLabel = (d) => {
  const dealId = d?.SupplyVendorDealId || d?.supplyVendorDealId || '';
  const dealName = d?.DealName || d?.dealName || '';
  if (dealName) return dealName;
  if (!dealId) return '';
  const vendor = supplyVendorName(d?.SupplyVendorId ?? d?.supplyVendorId);
  return vendor ? `${vendor} — ${dealId}` : dealId;
};

// TTD list ops. Removes delegate to the legacy applier (byte-identical for
// audiences / keywords / geo) plus a new MmgyDeals filter for pmp. Adds and
// edits exist for keywords (free-text 3P terms, resolved at launch) and pmp
// (id-entry SupplyVendorDealIds). Audiences and geo adds arrive from the detail
// panel's async picker as an encoded `<id><SEP><label>` (see
// decodeLookupValue) — the id is what makes them launchable, so an add without
// one is dropped rather than written as a name-only entry the launcher would
// silently skip.
function applyTtdListOps(edited, ag, fieldOps) {
  const dels = removesOf(fieldOps);
  applyTtdDeletions(edited, ag, dels);
  if (dels.pmp?.length && Array.isArray(ag.MmgyDeals)) {
    const drop = new Set(dels.pmp);
    ag.MmgyDeals = ag.MmgyDeals.filter((d) => !drop.has(ttdDealLabel(d)));
  }

  // keywords → MmgyAudiences.ThirdPartyAudiences (free-text strings).
  const kwOps = (fieldOps.keywords || []).filter((o) => o.op !== 'remove');
  if (kwOps.length) {
    const ma = ag.MmgyAudiences || (ag.MmgyAudiences = {});
    if (!Array.isArray(ma.ThirdPartyAudiences)) ma.ThirdPartyAudiences = [];
    for (const op of kwOps) {
      if (op.op === 'add') ma.ThirdPartyAudiences.push(op.value);
      else if (op.op === 'edit' && op.newValue) {
        const i = ma.ThirdPartyAudiences.indexOf(op.value);
        if (i >= 0) ma.ThirdPartyAudiences[i] = op.newValue;
      }
    }
  }

  // audiences → MmgyAudiences.ResolvedAudiences. The picker hands us the
  // ThirdPartyDataId + FullPath, which is exactly the pair the row builder
  // renders (r.FullPath) and the launcher assigns — so the add round-trips
  // and can be removed again by label.
  for (const op of (fieldOps.audiences || [])) {
    if (op.op !== 'add') continue;
    const { id, label } = decodeLookupValue(op.value);
    if (!id || !label) continue;
    const ma = ag.MmgyAudiences || (ag.MmgyAudiences = {});
    if (!Array.isArray(ma.ResolvedAudiences)) ma.ResolvedAudiences = [];
    if (ma.ResolvedAudiences.some((r) => r?.FullPath === label)) continue;
    ma.ResolvedAudiences.push({ ThirdPartyDataId: id, FullPath: label });
  }

  // geo → the shared CommonGeoLists entry's target / block GeoSegmentIds.
  // Same list the removes edit, and `Name` is the key ttdGeoLabel reads for a
  // segment that carries no Metro/City/Region/CountryName.
  for (const [field, listKey] of [['geoInclude', 'TargetGeoSegmentIds'], ['geoExclude', 'BlockGeoSegmentIds']]) {
    const writes = (fieldOps[field] || []).filter((o) => o.op === 'add' || o.op === 'edit');
    if (!writes.length) continue;
    const geoList = ttdGeoListFor(edited, ag);
    // No CommonGeoLists entry to write into (e.g. a copy template whose geo
    // came from the review_enrichment sidecar) — there's nothing the launcher
    // would read, so leave the plan untouched rather than invent a list.
    if (!geoList) continue;
    const bucket = geoList[listKey] || (geoList[listKey] = {});
    if (!Array.isArray(bucket.GeoSegmentIds)) bucket.GeoSegmentIds = [];
    for (const op of writes) {
      if (op.op === 'add') {
        const { id, label } = decodeLookupValue(op.value);
        if (!label) continue;
        if (bucket.GeoSegmentIds.some((s) => (id && String(s?.GeoSegmentId) === id) || ttdGeoLabel(s) === label)) continue;
        // A typed market name carries no GeoSegmentId, and that is fine: the
        // launcher resolves Name → GeoSegmentId when it builds the geo bid list.
        // Emitting a bare {Name} matches what the pipeline itself produces.
        bucket.GeoSegmentIds.push(id ? { GeoSegmentId: id, Name: label } : { Name: label });
      } else {
        const from = decodeLookupValue(op.value).label;
        const { id: newId, label: newLabel } = decodeLookupValue(op.newValue);
        if (!newLabel) continue;
        const hit = bucket.GeoSegmentIds.find((s) => ttdGeoLabel(s) === from);
        if (!hit) continue;
        // ttdGeoLabel prefers the per-level names, so a rename has to clear them
        // or the cell would keep rendering the OLD market. The id goes too: one
        // resolved for the old name does not describe the new one, and leaving it
        // would launch the market the user just renamed away from.
        delete hit.MetroName;
        delete hit.CityName;
        delete hit.RegionName;
        delete hit.CountryName;
        delete hit.GeoSegmentId;
        hit.Name = newLabel;
        if (newId) hit.GeoSegmentId = newId;
      }
    }
  }

  // pmp → MmgyDeals. Adds push a bare SupplyVendorDealId entry; edits
  // rewrite the id (and DealName, when the entry carries one, so the label
  // re-renders as typed) on the entry whose rendered label matched.
  for (const op of (fieldOps.pmp || [])) {
    if (op.op === 'add') {
      if (!Array.isArray(ag.MmgyDeals)) ag.MmgyDeals = [];
      ag.MmgyDeals.push({ SupplyVendorDealId: op.value });
    } else if (op.op === 'edit' && op.newValue && Array.isArray(ag.MmgyDeals)) {
      const hit = ag.MmgyDeals.find((d) => ttdDealLabel(d) === op.value);
      if (hit) {
        // Write the id-key spelling the entry already carries.
        if ('supplyVendorDealId' in hit && !('SupplyVendorDealId' in hit)) hit.supplyVendorDealId = op.newValue;
        else hit.SupplyVendorDealId = op.newValue;
        if (hit.DealName) hit.DealName = op.newValue;
        else if (hit.dealName) hit.dealName = op.newValue;
      }
    }
  }
}

// DV360 list deletions. Audiences/keywords are both settings entries under
// includedFirstPartyAndPartnerAudienceGroups (keyword rows carry
// thirdpartyAudienceId '1000'); interests live under
// includedGoogleAudienceGroup. Geo label sources are includedCountries
// (numeric IDs → countryName), includedOthers (MMGY DMA strings),
// includedCities and includedRegions, plus the matching excluded* arrays.
// The array-vs-single-object tolerance mirrors what dv360RowBuilders reads.
function applyDv360Deletions(li, dels) {
  const dropAud = new Set(dels.audiences || []);
  const dropKw = new Set(dels.keywords || []);
  if (dropAud.size || dropKw.size) {
    const ag = li.audiences?.includedFirstPartyAndPartnerAudienceGroups;
    const filterSettings = (settings) => (settings || []).filter((s) => {
      if (!s) return true;
      if (String(s.thirdpartyAudienceId) === '1000' && s.displayName) {
        return !dropKw.has(s.displayName);
      }
      if (String(s.thirdpartyAudienceId) === 'RESOLVED' || s.firstPartyAndPartnerAudienceId) {
        return !dropAud.has(s.displayName);
      }
      // Name-only entries (panel adds, resolved by name at launch) — no
      // pre-existing plan carries these, so legacy behaviour is unchanged.
      if (s.displayName && s.thirdpartyAudienceId == null) {
        return !dropAud.has(s.displayName);
      }
      return true;
    });
    if (Array.isArray(ag)) {
      for (const g of ag) g.settings = filterSettings(g.settings);
    } else if (ag && Array.isArray(ag.settings)) {
      ag.settings = filterSettings(ag.settings);
    }
  }
  const dropInt = new Set(dels.interests || []);
  if (dropInt.size && Array.isArray(li.audiences?.includedGoogleAudienceGroup?.settings)) {
    li.audiences.includedGoogleAudienceGroup.settings = li.audiences.includedGoogleAudienceGroup.settings
      .filter((s) => !dropInt.has(String(s?.googleAudienceId)));
  }
  const dropGeoInc = new Set(dels.geoInclude || []);
  const dropGeoExc = new Set(dels.geoExclude || []);
  if ((dropGeoInc.size || dropGeoExc.size) && li.geoTargeting) {
    const gt = li.geoTargeting;
    if (dropGeoInc.size) {
      if (Array.isArray(gt.includedCountries)) gt.includedCountries = gt.includedCountries.filter((id) => !dropGeoInc.has(countryName(id)));
      if (Array.isArray(gt.includedOthers)) gt.includedOthers = gt.includedOthers.filter((v) => !dropGeoInc.has(v));
      if (Array.isArray(gt.includedCities)) gt.includedCities = gt.includedCities.filter((v) => !dropGeoInc.has(v));
      if (Array.isArray(gt.includedRegions)) gt.includedRegions = gt.includedRegions.filter((v) => !dropGeoInc.has(v));
    }
    if (dropGeoExc.size) {
      if (Array.isArray(gt.excludedCountries)) gt.excludedCountries = gt.excludedCountries.filter((id) => !dropGeoExc.has(countryName(id)));
      if (Array.isArray(gt.excludedOthers)) gt.excludedOthers = gt.excludedOthers.filter((v) => !dropGeoExc.has(v));
      if (Array.isArray(gt.excludedCities)) gt.excludedCities = gt.excludedCities.filter((v) => !dropGeoExc.has(v));
      if (Array.isArray(gt.excludedRegions)) gt.excludedRegions = gt.excludedRegions.filter((v) => !dropGeoExc.has(v));
    }
  }
}

// ─── DV360 list ops ───────────────────────────────────────────────────

// Reverse of COUNTRY_BY_ID: lowercased country name → DV360 geo id, so a
// typed geo add that names a known country lands in included/excludedCountries
// (which the launcher pushes as-is) rather than the name-resolution buckets.
const COUNTRY_ID_BY_NAME = Object.fromEntries(
  Object.entries(COUNTRY_BY_ID).map(([id, name]) => [String(name).toLowerCase(), id]),
);

// How a pmpInventorySource entry renders in the pmp cell (see
// buildDv360ReviewRows): displayName wins, else the inventorySourceId.
const dv360PmpLabel = (s) => String((s && (s.displayName || s.inventorySourceId)) || '');

// DV360 list ops. Removes delegate to the legacy applier (byte-identical),
// with two extensions: keyword removes whose label lives in
// contextualTargeting are filtered there instead of the legacy '1000' seed
// path, and pmp removes filter li.pmpInventorySource by rendered label.
//
// Adds/edits:
//  - keywords    → li.contextualTargeting.includedContextualTargeting — the
//                  free-text keyword list the launcher consumes (already
//                  whitelisted by the Python preprocessor/validator). An edit
//                  of a legacy seed-sourced keyword retires the seed entry
//                  and writes the replacement to contextualTargeting.
//  - audiences   → name-only settings entries under
//                  includedFirstPartyAndPartnerAudienceGroups, resolved by
//                  name at launch; edits rewrite the matched displayName.
//  - interests   → { googleAudienceId } entries under includedGoogleAudienceGroup.
//  - geo         → country names → included/excludedCountries ids; anything
//                  else → includedOthers/excludedOthers (the launcher resolves
//                  names via search_geo_region). Edit = remove(old) + add(new).
//  - pmp         → { inventorySourceId } entries on li.pmpInventorySource.
/**
 * List ops for a row whose targeting came from a COPY's targeting_plan sidecar.
 *
 * Rewrites the SIDECAR, never li.geoTargeting / li.audiences. Creating those keys
 * is what makes a copy target twice: dv360_line_item_creator applies geoTargeting
 * at create and dv360_api_handler applies targeting_plan afterwards, so a line
 * item carrying both gets the union. Everything here therefore lands where the
 * launcher already looks.
 *
 *   remove -> prune the entry out of targeting_plan[rowId]
 *   add    -> geo only, staged into geo_plan[rowId] as a NAME (additive), because
 *             only the launcher can resolve a market name to a targetingOptionId
 *
 * Returns the fieldOps that were NOT handled here, so a row that mixes sidecar
 * targeting with template-stated cells still gets the normalized applier for the
 * rest. `row` is the review row, which carries targetingEntries + the field list.
 */
function applyDv360PlanListOps(edited, rowId, fieldOps, row) {
  const handledFields = new Set(row?.targetingFromPlanFields || []);
  if (!handledFields.size) return fieldOps;
  const entriesByField = row?.targetingEntries || {};
  const rest = {};

  for (const [field, ops] of Object.entries(fieldOps || {})) {
    // geoInclude / geoExclude are the panel's two sub-fields for the `geo` cell.
    const isGeoSub = field === 'geoInclude' || field === 'geoExclude';
    const planField = isGeoSub ? 'geo' : field;
    if (!handledFields.has(field) && !(isGeoSub && handledFields.has('geo'))) {
      rest[field] = ops;
      continue;
    }
    const excluded = field === 'geoExclude';

    for (const op of (Array.isArray(ops) ? ops : [])) {
      if (op.op === 'add') {
        // Only geo can be added: every other type needs a DV360 option-id lookup
        // the grid cannot do, which is why the panel offers no add for them.
        if (!isGeoSub) continue;
        edited.geo_plan = addGeoToPlan(edited.geo_plan, rowId, op.value, { excluded });
        continue;
      }
      if (op.op !== 'remove') continue;

      // A staged NAME (added above, not yet resolved) lives in geo_plan; a
      // resolved entry lives in targeting_plan. Try the staged one first so a
      // reviewer can take back an add they just made.
      if (isGeoSub) {
        const { geoPlan, removed } = removeGeoFromPlan(edited.geo_plan, rowId, op.value, { excluded });
        edited.geo_plan = geoPlan;
        // Only a REAL removal ends the op. Testing object identity here made every
        // resolved-geo × a no-op as soon as the row had any staged name at all.
        if (removed) continue;
      }

      const candidates = Array.isArray(entriesByField[planField])
        ? entriesByField[planField] : [];
      // Prefer the entry on the SAME side, so a market that is both included and
      // excluded resolves to the one whose × was clicked.
      const entry = candidates.find((e) => e.label === op.value && Boolean(e.excluded) === excluded)
        || candidates.find((e) => e.label === op.value);
      // A staged name has no plan option to prune — the geo_plan removal above is
      // the whole operation.
      if (entry?.staged) continue;
      if (!entry) continue;
      const plan = edited.targeting_plan?.[rowId];
      if (!plan) continue;
      const next = removeFromPlan(plan, entry);
      if (next.length) edited.targeting_plan[rowId] = next;
      else delete edited.targeting_plan[rowId];
    }
  }
  return rest;
}

function applyDv360ListOps(li, fieldOps) {
  const dels = removesOf(fieldOps);

  // Keyword removes: partition between the contextualTargeting strings and
  // the legacy seed entries so each source is filtered where it lives.
  if (dels.keywords?.length && Array.isArray(li.contextualTargeting?.includedContextualTargeting)) {
    const ctx = li.contextualTargeting.includedContextualTargeting;
    const inCtx = new Set(ctx.map(String));
    const ctxDrops = new Set(dels.keywords.filter((v) => inCtx.has(v)));
    if (ctxDrops.size) {
      li.contextualTargeting.includedContextualTargeting = ctx.filter((v) => !ctxDrops.has(String(v)));
      dels.keywords = dels.keywords.filter((v) => !ctxDrops.has(v));
      if (!dels.keywords.length) delete dels.keywords;
    }
  }
  applyDv360Deletions(li, dels);
  if (dels.pmp?.length && Array.isArray(li.pmpInventorySource)) {
    const drop = new Set(dels.pmp);
    li.pmpInventorySource = li.pmpInventorySource.filter((s) => !drop.has(dv360PmpLabel(s)));
  }

  // keywords → contextualTargeting.
  const kwTarget = () => {
    const ct = li.contextualTargeting || (li.contextualTargeting = {});
    if (!Array.isArray(ct.includedContextualTargeting)) ct.includedContextualTargeting = [];
    return ct.includedContextualTargeting;
  };
  const seedSettings = () => {
    const groups = li.audiences?.includedFirstPartyAndPartnerAudienceGroups;
    return Array.isArray(groups)
      ? groups.flatMap((g) => (Array.isArray(g?.settings) ? g.settings : []))
      : (groups && Array.isArray(groups.settings) ? groups.settings : []);
  };
  for (const op of (fieldOps.keywords || [])) {
    if (op.op === 'add') kwTarget().push(op.value);
    else if (op.op === 'edit' && op.newValue) {
      const list = kwTarget();
      const i = list.findIndex((v) => String(v) === op.value);
      if (i >= 0) { list[i] = op.newValue; continue; }
      // Seed-sourced keyword: retire the '1000' seed entry and write the
      // replacement where the launcher reads keywords from.
      const isSeed = seedSettings().some((s) => String(s?.thirdpartyAudienceId) === '1000' && s?.displayName === op.value);
      if (isSeed) {
        applyDv360Deletions(li, { keywords: [op.value] });
        list.push(op.newValue);
      }
    }
  }

  // audiences → 1P/partner audience group settings. Adds use the single-
  // object (MMGY) shape when creating from scratch — the same shape the row
  // builder reads; edits search every group.
  const audAddTarget = () => {
    const a = li.audiences || (li.audiences = {});
    let g = a.includedFirstPartyAndPartnerAudienceGroups;
    if (!g) g = a.includedFirstPartyAndPartnerAudienceGroups = { settings: [] };
    if (Array.isArray(g)) {
      if (!g.length) g.push({ settings: [] });
      if (!Array.isArray(g[0].settings)) g[0].settings = [];
      return g[0].settings;
    }
    if (!Array.isArray(g.settings)) g.settings = [];
    return g.settings;
  };
  for (const op of (fieldOps.audiences || [])) {
    if (op.op === 'add') audAddTarget().push({ displayName: op.value });
    else if (op.op === 'edit' && op.newValue) {
      const hit = seedSettings().find((s) => s && s.displayName === op.value
        && String(s.thirdpartyAudienceId) !== '1000');
      if (hit) hit.displayName = op.newValue;
    }
  }

  // interests → Google audience group settings ({ googleAudienceId }).
  const intTarget = () => {
    const a = li.audiences || (li.audiences = {});
    const g = a.includedGoogleAudienceGroup || (a.includedGoogleAudienceGroup = {});
    if (!Array.isArray(g.settings)) g.settings = [];
    return g.settings;
  };
  for (const op of (fieldOps.interests || [])) {
    if (op.op === 'add') intTarget().push({ googleAudienceId: op.value });
    else if (op.op === 'edit' && op.newValue) {
      const hit = intTarget().find((s) => String(s?.googleAudienceId) === op.value);
      if (hit) hit.googleAudienceId = op.newValue;
    }
  }

  // geo → countries (by name match) or the *Others name buckets.
  const geoAdd = (value, excluded) => {
    const gt = li.geoTargeting || (li.geoTargeting = {});
    const countryId = COUNTRY_ID_BY_NAME[String(value).trim().toLowerCase()];
    const key = countryId
      ? (excluded ? 'excludedCountries' : 'includedCountries')
      : (excluded ? 'excludedOthers' : 'includedOthers');
    if (!Array.isArray(gt[key])) gt[key] = [];
    gt[key].push(countryId || value);
  };
  for (const [field, excluded] of [['geoInclude', false], ['geoExclude', true]]) {
    for (const op of (fieldOps[field] || [])) {
      if (op.op === 'add') geoAdd(op.value, excluded);
      else if (op.op === 'edit' && op.newValue) {
        // Edit = remove(old) + add(new): the old label can live in any of
        // the four per-direction arrays, and the new value re-routes by kind.
        applyDv360Deletions(li, { [field]: [op.value] });
        geoAdd(op.newValue, excluded);
      }
    }
  }

  // pmp → li.pmpInventorySource.
  for (const op of (fieldOps.pmp || [])) {
    if (op.op === 'add') {
      if (!Array.isArray(li.pmpInventorySource)) li.pmpInventorySource = [];
      li.pmpInventorySource.push({ inventorySourceId: op.value });
    } else if (op.op === 'edit' && op.newValue && Array.isArray(li.pmpInventorySource)) {
      const hit = li.pmpInventorySource.find((s) => dv360PmpLabel(s) === op.value);
      if (hit) {
        hit.inventorySourceId = op.newValue;
        if (hit.displayName) hit.displayName = op.newValue;
      }
    }
  }
}

/**
 * TTD / DV360 / Google Ads: overlay scalar edits + pending list ops onto a
 * deep clone of launcherResult. `listOpsOrDeletions` accepts both the
 * current pendingListOps shape ({ rowId: { field: [{op,value,newValue?}] } })
 * and the legacy pendingDeletions shape ({ rowId: { field: [label] } }) —
 * legacy string arrays are normalized to remove ops internally. `reviewRows`
 * is used only to skip rowIds the grid doesn't know about; rows we can't
 * resolve to an underlying entity are left alone so the caller still PUTs
 * the original rather than dropping edits silently.
 */
export function applyLauncherResultEdits(launcherResult, edits, listOpsOrDeletions, reviewRows = [], rowOps = []) {
  const edited = JSON.parse(JSON.stringify(launcherResult || {}));
  if (isYahooResult(edited)) return applyYahooEdits(edited, edits, listOpsOrDeletions, reviewRows, rowOps);
  const platform = String(edited.platform || edited.Platform || '').toLowerCase();
  // Google Ads plans (Search / PMax / Demand Gen) have their own entity shape
  // and positional row ids — delegate before the TTD-vs-DV360 split, which
  // would otherwise treat them as DV360 and find no lineItems to write to.
  // gadsEdits speaks the full op shape (it normalizes legacy label arrays the
  // same way this module does), so ops pass through untouched.
  if (isGadsPlatform(platform) || (!platform && isGadsLauncherResult(edited))) {
    return applyGadsEdits(edited, edits, listOpsOrDeletions, reviewRows, rowOps);
  }
  // Facebook, same reasoning: campaign → ad_sets is its own entity shape with
  // positional row ids, and the DV360 branch below would look for `lineItems`,
  // find none, and drop every edit silently. The FB pipeline does not stamp
  // `platform` on parsed_response, so the shape test is the live path.
  if (isFbPlatform(platform) || (!platform && isFbLauncherResult(edited))) {
    return applyFbEdits(edited, edits, listOpsOrDeletions, reviewRows, rowOps);
  }
  const isTtd = platform === 'ttd';
  // Match both id casings — the row builders accept camelCase and PascalCase
  // entity ids (MMGY vs copy templates), so the applier must too or edits on
  // PascalCase templates are silently dropped.
  const findEntity = (rowId) => (isTtd
    ? (edited.AdGroups || []).find((a) => a.adGroupId === rowId || a.AdGroupId === rowId)
    : (edited.lineItems || []).find((x) => x.lineItemId === rowId || x.LineItemId === rowId));

  for (const [rowId, fields] of Object.entries(edits || {})) {
    if (reviewRows.length && !reviewRows.some((r) => r.rowId === rowId)) continue;
    const entity = findEntity(rowId);
    if (!entity) continue;
    if (isTtd) applyTtdFields(edited, entity, rowId, fields);
    else applyDv360Fields(edited, entity, rowId, fields);
  }

  for (const [rowId, fields] of Object.entries(listOpsOrDeletions || {})) {
    const entity = findEntity(rowId);
    if (!entity) continue;
    const fieldOps = normalizeRowOps(fields);
    if (isTtd) { applyTtdListOps(edited, entity, fieldOps); continue; }
    // A COPY's targeting lives in the sidecar; those fields are handled there and
    // whatever is left over still goes through the normalized applier.
    const row = reviewRows.find((r) => r.rowId === rowId);
    const remaining = row?.targetingFromPlan
      ? applyDv360PlanListOps(edited, rowId, fieldOps, row)
      : fieldOps;
    applyDv360ListOps(entity, remaining);
  }

  // Row-level ops run LAST, after every value edit has landed on the rows that
  // existed when the batch was staged. Doing it the other way round would mean
  // resolving row ids against an array a delete had already renumbered. Each
  // clone's overrides then go through the SAME field applier a typed edit uses,
  // so a cloned row's values are written by one code path, not two.
  if (Array.isArray(rowOps) && rowOps.length) {
    const { created } = isTtd
      ? applyTtdRowOps(edited, rowOps)
      : applyDv360RowOps(edited, rowOps);
    for (const { rowId, overrides } of created) {
      if (!overrides || !Object.keys(overrides).length) continue;
      const entity = findEntity(rowId);
      if (!entity) continue;
      if (isTtd) applyTtdFields(edited, entity, rowId, overrides);
      else applyDv360Fields(edited, entity, rowId, overrides);
    }
  }
  return edited;
}
